import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Button, Input, message, Avatar } from 'antd';
import { axios } from '../../../../../axiosConfig';


const { Search } = Input;

const MergeContactModal = ({ 
  visible, 
  onCancel, 
  contactToMerge, 
  onMergeSuccess 
}) => {
  const [availableContacts, setAvailableContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Cargar contactos disponibles para fusionar
  const loadAvailableContacts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/${contactToMerge.id}/available-for-merge`);
      setAvailableContacts(response.data.data || []);
      setFilteredContacts(response.data.data || []);
    } catch (error) {
      console.error('Error cargando contactos disponibles:', error);
      message.error('Error al cargar contactos disponibles para fusionar');
    } finally {
      setLoading(false);
    }
  }, [contactToMerge]);

  useEffect(() => {
    if (visible && contactToMerge?.id) {
      loadAvailableContacts();
    }
  }, [visible, contactToMerge, loadAvailableContacts]);

  // Filtrar contactos según búsqueda
  useEffect(() => {
    if (searchText) {
      const filtered = availableContacts.filter(contact => {
        const fullName = `${contact.name || ''} ${contact.surname || ''}`.toLowerCase();
        const email = (contact.email || '').toLowerCase();
        const search = searchText.toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(availableContacts);
    }
  }, [searchText, availableContacts]);

  const handleMerge = async () => {
    if (!selectedContact) {
      message.warning('Por favor selecciona un contacto para fusionar');
      return;
    }

    setMerging(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/merge`, {
        mainContactId: contactToMerge.id,
        contactToMergeId: selectedContact.id
      });
      
      message.success('Contactos fusionados exitosamente');
      onMergeSuccess && onMergeSuccess();
      handleCancel();
    } catch (error) {
      console.error('Error fusionando contactos:', error);
      message.error('Error al fusionar contactos');
    } finally {
      setMerging(false);
    }
  };

  const handleCancel = () => {
    setSelectedContact(null);
    setSearchText('');
    setAvailableContacts([]);
    setFilteredContacts([]);
    onCancel();
  };

  const columns = [
    {
      title: 'Contacto',
      key: 'contact',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {record.picture && (
            <Avatar src={record.picture} size="small" />
          )}
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {record.name} {record.surname || ''}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.email}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Teléfono',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '-'
    },
    {
      title: 'Posiciones',
      key: 'positions',
      render: (_, record) => (
        <div>
          {record.positions && record.positions.length > 0 ? (
            record.positions.map((position, index) => (
              <div key={index} style={{ fontSize: '12px' }}>
                {position.title} en {position.entity?.name}
              </div>
            ))
          ) : (
            '-'
          )}
        </div>
      )
    }
  ];

  const rowSelection = {
    type: 'radio',
    selectedRowKeys: selectedContact ? [selectedContact.id] : [],
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedContact(selectedRows[0] || null);
    }
  };

  return (
    <Modal
      title={`Fusionar contacto: ${contactToMerge?.name} ${contactToMerge?.surname || ''}`}
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancelar
        </Button>,
        <Button 
          key="merge" 
          type="primary" 
          onClick={handleMerge}
          loading={merging}
          disabled={!selectedContact}
        >
          Fusionar Contactos
        </Button>
      ]}
    >
      <div style={{ marginBottom: '16px' }}>
        <p>
          Selecciona el contacto que deseas fusionar con <strong>{contactToMerge?.name} {contactToMerge?.surname || ''}</strong>. 
          El contacto seleccionado será fusionado y sus datos se transferirán al contacto principal.
        </p>
        
        <Search
          placeholder="Buscar contactos..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: '16px' }}
        />
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredContacts}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: false
        }}
        size="small"
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};

export default MergeContactModal;
